import PriceStackLoyaltyProminentPrice from 'components/Luxkit/PricePoints/Loyalty/PriceStackLoyaltyProminentPrice'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import React, { useMemo } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { isActiveLuxuryPlusMember, isLuxuryPlusEnabled } from 'loyalty/selectors/featureToggle'
import PriceStackPriceWithLoyalty from 'components/Luxkit/PricePoints/PriceStackPriceWithLoyalty'
import BasePriceRowPrice from 'components/Luxkit/PricePoints/BasePriceRowPrice'
import PriceRowLoyaltyPriceBox from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPriceBox'

type PriceProps = React.ComponentProps<typeof BasePriceRowPrice>
interface Props {
  size: 'S' | 'L';
  price: number;
  saleUnit: string;
  taxesAndFees: PriceProps['taxesAndFees'];
  memberPrice?: number;
  testid?: string;
  signDisplay?: PriceProps['signDisplay'];
  horizontalAlign?: 'start' | 'center' | 'end';
  desktopHorizontalAlign?: 'start' | 'center' | 'end';
  orientation?: 'horizontal' | 'vertical';
  loyaltyConfig?: App.OfferLoyaltyConfig;
}

function LuxuryPlusPriceStack(props: Props) {
  const {
    memberPrice = 0,
    price,
    saleUnit,
    size,
    taxesAndFees,
    testid,
    signDisplay,
    horizontalAlign = 'start',
    desktopHorizontalAlign,
    orientation = 'vertical',
    loyaltyConfig,
  } = props

  const isMember = useAppSelector(isActiveLuxuryPlusMember)
  const luxPlusEnabled = useAppSelector(isLuxuryPlusEnabled)

  const mode = useMemo(() => {
    if (!luxPlusEnabled) {
      return 'regular-only'
    } else if (!isMember && memberPrice > 0) {
      return 'regular-prominant'
    } else if (loyaltyConfig?.access === 'memberOnly') {
      return 'member-only'
    } else if (isMember && memberPrice > 0) {
      return 'member-prominant'
    } else {
      return 'regular-only'
    }
  }, [isMember, loyaltyConfig?.access, luxPlusEnabled, memberPrice])

  switch (mode) {
    case 'member-only':
      return <PriceRowLoyaltyPriceBox
        data-testid={testid}
        // we're still not sure if member only will fill in price or member price
        memberPrice={memberPrice || price}
        saleUnit={saleUnit}
        taxesAndFees={taxesAndFees}
        size={size}
        horizontalAlign={horizontalAlign}
      />
    case 'regular-only':
      return <PriceRowPrice
        data-testid={testid}
        price={price}
        saleUnit={saleUnit}
        taxesAndFees={taxesAndFees}
        size={size}
        signDisplay={signDisplay}
      />
    case 'member-prominant':
      return <PriceStackLoyaltyProminentPrice
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        taxesAndFees={taxesAndFees}
        nonMemberPrice={price}
        size={size}
        memberPriceSignDisplay={signDisplay}
        nonMemberPriceSignDisplay={signDisplay}
        orientation={orientation}
        horizontalAlign={horizontalAlign}
        desktopHorizontalAlign={desktopHorizontalAlign}
      />
    case 'regular-prominant':
      return <PriceStackPriceWithLoyalty
        data-testid={testid}
        memberPrice={memberPrice}
        saleUnit={saleUnit}
        taxesAndFees={taxesAndFees}
        nonMemberPrice={price}
        size={size}
        signDisplay={signDisplay}
        horizontalAlign={horizontalAlign}
        desktopHorizontalAlign={desktopHorizontalAlign}
      />
  }
}

export default LuxuryPlusPriceStack
